import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TopBar } from '../components/layout/TopBar';
import { useAppStore } from '../store/useStore';
import { customersAPI, reservationsAPI, promoCodesAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  Plus, 
  MoreVertical, 
  Search, 
  User, 
  Phone, 
  Mail,
  Calendar,
  AlertTriangle,
  Edit2,
  Trash2,
  Eye,
  Star,
  TrendingUp,
  DollarSign,
  Users,
  Gift,
  Heart,
  Utensils,
  MapPin,
  Clock,
  Award,
  RefreshCw,
  Tag,
  Send,
  MessageSquare,
  Percent,
  Download,
  FileText,
  FileSpreadsheet
} from 'lucide-react';

const ALLERGY_OPTIONS = [
  'Gluten', 'Lácteos', 'Mariscos', 'Nueces', 'Huevo', 'Soya', 'Pescado', 'Cacahuate'
];

const DIETARY_OPTIONS = [
  'Vegetariano', 'Vegano', 'Sin gluten', 'Kosher', 'Halal', 'Keto', 'Sin lactosa'
];

const SEATING_OPTIONS = [
  { value: 'interior', label: 'Interior' },
  { value: 'terraza', label: 'Terraza' },
  { value: 'bar', label: 'Bar' },
  { value: 'privado', label: 'Privado' },
  { value: 'ventana', label: 'Junto a ventana' },
];

export default function Customers() {
  const { t } = useTranslation();
  const { customers, setCustomers } = useAppStore();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTag, setFilterTag] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerHistory, setCustomerHistory] = useState([]);
  const [stats, setStats] = useState(null);
  const [birthdayCustomers, setBirthdayCustomers] = useState([]);
  const [dialogTab, setDialogTab] = useState('basic');
  
  // Coupon modal states
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [couponCustomer, setCouponCustomer] = useState(null);
  const [promoCodes, setPromoCodes] = useState([]);
  const [selectedPromoCode, setSelectedPromoCode] = useState('');
  const [sendVia, setSendVia] = useState('email');
  const [sendingCoupon, setSendingCoupon] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
    tags: [],
    allergies: [],
    dietary_restrictions: [],
    seating_preference: '',
    accepts_marketing: true,
    preferred_contact: 'email',
    birthday: '',
    anniversary: '',
  });

  useEffect(() => {
    fetchCustomers();
    fetchStats();
    fetchBirthdays();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await customersAPI.list({ search: searchTerm || undefined });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Error al cargar clientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await customersAPI.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchBirthdays = async () => {
    try {
      const response = await customersAPI.getBirthdays(30);
      setBirthdayCustomers(response.data);
    } catch (error) {
      console.error('Error fetching birthdays:', error);
    }
  };

  const fetchPromoCodes = async () => {
    try {
      const response = await promoCodesAPI.list();
      // Filter only active promo codes
      const activeCodes = (response.data || []).filter(
        code => code.status === 'active'
      );
      setPromoCodes(activeCodes);
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    }
  };

  const openCouponDialog = (customer) => {
    setCouponCustomer(customer);
    setSelectedPromoCode('');
    // Set default send method based on available contact info
    if (customer.email && customer.phone) {
      setSendVia('both');
    } else if (customer.email) {
      setSendVia('email');
    } else if (customer.phone) {
      setSendVia('sms');
    } else {
      setSendVia('email');
    }
    fetchPromoCodes();
    setShowCouponDialog(true);
  };

  const handleSendCoupon = async () => {
    if (!selectedPromoCode) {
      toast.error('Selecciona un cupón');
      return;
    }
    
    setSendingCoupon(true);
    try {
      const response = await customersAPI.sendCoupon(
        couponCustomer.id,
        selectedPromoCode,
        sendVia
      );
      
      if (response.data.success) {
        toast.success(response.data.message);
        setShowCouponDialog(false);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar cupón');
    } finally {
      setSendingCoupon(false);
    }
  };

  const handleExport = async (format) => {
    setExporting(true);
    try {
      const response = await customersAPI.export(format, { tag: activeTab !== 'all' ? activeTab : undefined });
      
      if (format === 'csv') {
        // CSV comes as text
        const blob = new Blob([response.data], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clientes_${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        // PDF comes as blob
        const url = window.URL.createObjectURL(response.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `clientes_${new Date().toISOString().split('T')[0]}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
      toast.success(`Exportado a ${format.toUpperCase()}`);
    } catch (error) {
      toast.error('Error al exportar');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => {
      fetchCustomers();
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchTerm]);

  // Filter customers
  const filteredCustomers = useMemo(() => {
    return customers.filter(c => {
      if (filterTag === 'all') return true;
      if (filterTag === 'vip') return c.tags?.includes('VIP');
      if (filterTag === 'frequent') return c.tags?.includes('Frecuente');
      if (filterTag === 'new') return (c.total_visits || 0) <= 1;
      if (filterTag === 'noshow') return (c.no_shows || 0) > 0;
      return true;
    });
  }, [customers, filterTag]);

  const openCreateDialog = () => {
    setSelectedCustomer(null);
    setFormData({
      name: '', email: '', phone: '', notes: '', tags: [],
      allergies: [], dietary_restrictions: [], seating_preference: '',
      accepts_marketing: true, preferred_contact: 'email', birthday: '', anniversary: ''
    });
    setDialogTab('basic');
    setShowDialog(true);
  };

  const openEditDialog = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      name: customer.name,
      email: customer.email || '',
      phone: customer.phone || '',
      notes: customer.notes || '',
      tags: customer.tags || [],
      allergies: customer.allergies || [],
      dietary_restrictions: customer.dietary_restrictions || [],
      seating_preference: customer.seating_preference || '',
      accepts_marketing: customer.accepts_marketing !== false,
      preferred_contact: customer.preferred_contact || 'email',
      birthday: customer.birthday || '',
      anniversary: customer.anniversary || '',
    });
    setDialogTab('basic');
    setShowDialog(true);
  };

  const openDetailsDialog = async (customer) => {
    setSelectedCustomer(customer);
    try {
      const response = await reservationsAPI.list({ customer_id: customer.id });
      setCustomerHistory(response.data || []);
    } catch (error) {
      setCustomerHistory([]);
    }
    setShowDetailsDialog(true);
  };

  const handleSubmit = async () => {
    try {
      if (selectedCustomer) {
        const response = await customersAPI.update(selectedCustomer.id, formData);
        setCustomers(customers.map(c => c.id === selectedCustomer.id ? response.data : c));
        toast.success('Cliente actualizado');
      } else {
        const response = await customersAPI.create(formData);
        setCustomers([...customers, response.data]);
        toast.success('Cliente creado');
      }
      setShowDialog(false);
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (customerId) => {
    try {
      await customersAPI.delete(customerId);
      setCustomers(customers.filter(c => c.id !== customerId));
      toast.success('Cliente eliminado');
      fetchStats();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const toggleTag = (tag) => {
    if (formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
    } else {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const toggleAllergy = (allergy) => {
    if (formData.allergies.includes(allergy)) {
      setFormData({ ...formData, allergies: formData.allergies.filter(a => a !== allergy) });
    } else {
      setFormData({ ...formData, allergies: [...formData.allergies, allergy] });
    }
  };

  const toggleDietary = (item) => {
    if (formData.dietary_restrictions.includes(item)) {
      setFormData({ ...formData, dietary_restrictions: formData.dietary_restrictions.filter(d => d !== item) });
    } else {
      setFormData({ ...formData, dietary_restrictions: [...formData.dietary_restrictions, item] });
    }
  };

  return (
    <div className="min-h-screen" data-testid="customers-page">
      <TopBar 
        title="CRM - Clientes" 
        helpSection="customers"
        actions={
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="sm" variant="outline" className="rounded-xl" disabled={exporting} data-testid="export-btn">
                  <Download className={cn("h-4 w-4 mr-1", exporting && "animate-spin")} />
                  Exportar
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleExport('csv')}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  Exportar CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('pdf')}>
                  <FileText className="h-4 w-4 mr-2" />
                  Exportar PDF
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button size="sm" variant="outline" onClick={() => { fetchCustomers(); fetchStats(); }} className="rounded-xl">
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button size="sm" onClick={openCreateDialog} className="rounded-xl" data-testid="new-customer-btn">
              <Plus className="h-4 w-4 mr-1" /> Nuevo Cliente
            </Button>
          </div>
        }
      />

      {/* Stats Cards */}
      {stats && (
        <div className="px-6 pt-6 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.total_customers}</p>
                  <p className="text-xs text-muted-foreground">Total Clientes</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-yellow-500/10">
                  <Star className="h-5 w-5 text-yellow-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.vip_count}</p>
                  <p className="text-xs text-muted-foreground">VIP</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-pink-500/10">
                  <Gift className="h-5 w-5 text-pink-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.birthday_this_month}</p>
                  <p className="text-xs text-muted-foreground">Cumpleaños</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/10">
                  <TrendingUp className="h-5 w-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.average_visits?.toFixed(1)}</p>
                  <p className="text-xs text-muted-foreground">Visitas Prom.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-green-500/10">
                  <DollarSign className="h-5 w-5 text-green-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${stats.average_spend?.toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">Gasto Prom.</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="border-0 shadow-sm">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-emerald-500/10">
                  <Award className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-2xl font-bold">${(stats.total_revenue / 1000)?.toFixed(1)}k</p>
                  <p className="text-xs text-muted-foreground">Ingresos Total</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="p-6">
        {/* Filters */}
        <div className="mb-6 flex flex-wrap items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre, teléfono o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 rounded-xl"
              data-testid="search-customers-input"
            />
          </div>
          
          <Select value={filterTag} onValueChange={setFilterTag}>
            <SelectTrigger className="w-40 rounded-xl">
              <SelectValue placeholder="Filtrar" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="vip">⭐ VIP</SelectItem>
              <SelectItem value="frequent">🔄 Frecuentes</SelectItem>
              <SelectItem value="new">🆕 Nuevos</SelectItem>
              <SelectItem value="noshow">⚠️ Con No-Shows</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Birthday Alert */}
        {birthdayCustomers.length > 0 && (
          <Card className="mb-6 border-pink-200 bg-pink-50 dark:bg-pink-950/20 dark:border-pink-900">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <Gift className="h-5 w-5 text-pink-500" />
                <div>
                  <p className="font-medium text-pink-700 dark:text-pink-300">
                    ¡Cumpleaños próximos!
                  </p>
                  <p className="text-sm text-pink-600 dark:text-pink-400">
                    {birthdayCustomers.map(c => c.name).join(', ')}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Customers Table */}
        <Card className="border-0 shadow-sm">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Cliente</TableHead>
                <TableHead>Contacto</TableHead>
                <TableHead className="text-center">Visitas</TableHead>
                <TableHead className="text-center">Gasto Total</TableHead>
                <TableHead className="text-center">Valor</TableHead>
                <TableHead>Tags</TableHead>
                <TableHead className="w-12"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No hay clientes que coincidan con la búsqueda
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow 
                    key={customer.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    data-testid={`customer-row-${customer.id}`}
                  >
                    <TableCell onClick={() => openDetailsDialog(customer)}>
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-xl flex items-center justify-center",
                          customer.tags?.includes('VIP') 
                            ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white" 
                            : "bg-primary/10"
                        )}>
                          {customer.tags?.includes('VIP') ? (
                            <Star className="h-5 w-5" />
                          ) : (
                            <User className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium">{customer.name}</p>
                          {customer.last_visit && (
                            <p className="text-xs text-muted-foreground">
                              Última visita: {customer.last_visit}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openDetailsDialog(customer)}>
                      <div className="space-y-1 text-sm">
                        {customer.phone && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Phone className="h-3 w-3" /> {customer.phone}
                          </div>
                        )}
                        {customer.email && (
                          <div className="flex items-center gap-1 text-muted-foreground">
                            <Mail className="h-3 w-3" /> {customer.email}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center" onClick={() => openDetailsDialog(customer)}>
                      <span className="font-semibold text-lg">{customer.total_visits || 0}</span>
                      {customer.no_shows > 0 && (
                        <Badge variant="destructive" className="ml-2 text-xs">
                          {customer.no_shows} NS
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-center" onClick={() => openDetailsDialog(customer)}>
                      <span className="font-semibold">${(customer.total_spent || 0).toLocaleString()}</span>
                    </TableCell>
                    <TableCell className="text-center" onClick={() => openDetailsDialog(customer)}>
                      <div className={cn(
                        "inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium",
                        (customer.customer_value_score || 0) >= 50 ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400" :
                        (customer.customer_value_score || 0) >= 20 ? "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400" :
                        "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
                      )}>
                        <TrendingUp className="h-3 w-3" />
                        {(customer.customer_value_score || 0).toFixed(0)}
                      </div>
                    </TableCell>
                    <TableCell onClick={() => openDetailsDialog(customer)}>
                      <div className="flex flex-wrap gap-1">
                        {(customer.tags || []).slice(0, 3).map((tag, i) => (
                          <Badge 
                            key={i} 
                            variant="secondary" 
                            className={cn(
                              "text-xs",
                              tag === 'VIP' && "bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400",
                              tag === 'Frecuente' && "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                            )}
                          >
                            {tag}
                          </Badge>
                        ))}
                        {customer.allergies?.length > 0 && (
                          <Badge variant="outline" className="text-xs text-orange-600 border-orange-300">
                            ⚠️ Alergias
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="rounded-xl" data-testid={`customer-actions-${customer.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetailsDialog(customer)}>
                            <Eye className="h-4 w-4 mr-2" /> Ver detalles
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(customer)}>
                            <Edit2 className="h-4 w-4 mr-2" /> Editar
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => openCouponDialog(customer)}
                            className="text-primary"
                          >
                            <Tag className="h-4 w-4 mr-2" /> Enviar cupón
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-destructive"
                            onClick={() => handleDelete(customer.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl" data-testid="customer-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedCustomer ? 'Editar Cliente' : 'Nuevo Cliente'}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={dialogTab} onValueChange={setDialogTab}>
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="basic">Básico</TabsTrigger>
              <TabsTrigger value="preferences">Preferencias</TabsTrigger>
              <TabsTrigger value="marketing">Marketing</TabsTrigger>
            </TabsList>

            <TabsContent value="basic" className="space-y-4">
              <div>
                <Label>Nombre completo *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre completo"
                  className="rounded-xl"
                  data-testid="customer-name-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+52 55 1234 5678"
                    className="rounded-xl"
                    data-testid="customer-phone-input"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="correo@ejemplo.com"
                    className="rounded-xl"
                    data-testid="customer-email-input"
                  />
                </div>
              </div>

              <div>
                <Label>Notas internas</Label>
                <Textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  placeholder="Observaciones del personal, historial, etc."
                  rows={3}
                  className="rounded-xl"
                  data-testid="customer-notes-textarea"
                />
              </div>

              <div>
                <Label className="mb-2 block">Etiquetas</Label>
                <div className="flex flex-wrap gap-2">
                  {['VIP', 'Frecuente', 'Nuevo', 'Corporativo', 'Influencer', 'Prensa'].map(tag => (
                    <Badge
                      key={tag}
                      variant={formData.tags.includes(tag) ? "default" : "outline"}
                      className={cn("cursor-pointer transition-all", formData.tags.includes(tag) && "bg-primary")}
                      onClick={() => toggleTag(tag)}
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="preferences" className="space-y-4">
              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-orange-500" /> Alergias
                </Label>
                <div className="flex flex-wrap gap-2">
                  {ALLERGY_OPTIONS.map(allergy => (
                    <Badge
                      key={allergy}
                      variant={formData.allergies.includes(allergy) ? "default" : "outline"}
                      className={cn(
                        "cursor-pointer transition-all",
                        formData.allergies.includes(allergy) && "bg-orange-500 hover:bg-orange-600"
                      )}
                      onClick={() => toggleAllergy(allergy)}
                    >
                      {allergy}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="mb-2 block flex items-center gap-2">
                  <Utensils className="h-4 w-4 text-green-500" /> Restricciones alimentarias
                </Label>
                <div className="flex flex-wrap gap-2">
                  {DIETARY_OPTIONS.map(item => (
                    <Badge
                      key={item}
                      variant={formData.dietary_restrictions.includes(item) ? "default" : "outline"}
                      className={cn(
                        "cursor-pointer transition-all",
                        formData.dietary_restrictions.includes(item) && "bg-green-500 hover:bg-green-600"
                      )}
                      onClick={() => toggleDietary(item)}
                    >
                      {item}
                    </Badge>
                  ))}
                </div>
              </div>

              <div>
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" /> Preferencia de zona
                </Label>
                <Select 
                  value={formData.seating_preference} 
                  onValueChange={(v) => setFormData({ ...formData, seating_preference: v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue placeholder="Seleccionar zona preferida" />
                  </SelectTrigger>
                  <SelectContent>
                    {SEATING_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </TabsContent>

            <TabsContent value="marketing" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Gift className="h-4 w-4 text-pink-500" /> Fecha de cumpleaños
                  </Label>
                  <Input
                    type="date"
                    value={formData.birthday}
                    onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
                <div>
                  <Label className="flex items-center gap-2">
                    <Heart className="h-4 w-4 text-red-500" /> Aniversario
                  </Label>
                  <Input
                    type="date"
                    value={formData.anniversary}
                    onChange={(e) => setFormData({ ...formData, anniversary: e.target.value })}
                    className="rounded-xl"
                  />
                </div>
              </div>

              <div>
                <Label>Contacto preferido</Label>
                <Select 
                  value={formData.preferred_contact} 
                  onValueChange={(v) => setFormData({ ...formData, preferred_contact: v })}
                >
                  <SelectTrigger className="rounded-xl">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="phone">Teléfono</SelectItem>
                    <SelectItem value="sms">SMS</SelectItem>
                    <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between p-4 rounded-xl bg-muted">
                <div>
                  <p className="font-medium">Acepta comunicaciones de marketing</p>
                  <p className="text-sm text-muted-foreground">Promociones, eventos especiales, etc.</p>
                </div>
                <Switch
                  checked={formData.accepts_marketing}
                  onCheckedChange={(v) => setFormData({ ...formData, accepts_marketing: v })}
                />
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)} className="rounded-xl">
              Cancelar
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={!formData.name}
              className="rounded-xl"
              data-testid="submit-customer-btn"
            >
              {selectedCustomer ? 'Guardar cambios' : 'Crear cliente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Details Dialog */}
      <Dialog open={showDetailsDialog} onOpenChange={setShowDetailsDialog}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="customer-details-dialog">
          <DialogHeader>
            <DialogTitle>Perfil del Cliente</DialogTitle>
          </DialogHeader>

          {selectedCustomer && (
            <div className="space-y-6">
              {/* Customer Info Header */}
              <div className="flex items-start gap-4">
                <div className={cn(
                  "w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0",
                  selectedCustomer.tags?.includes('VIP') 
                    ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white" 
                    : "bg-primary/10"
                )}>
                  {selectedCustomer.tags?.includes('VIP') ? (
                    <Star className="h-8 w-8" />
                  ) : (
                    <User className="h-8 w-8 text-primary" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-xl">{selectedCustomer.name}</h3>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {(selectedCustomer.tags || []).map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">{tag}</Badge>
                    ))}
                  </div>
                  <div className="space-y-1 mt-3 text-sm text-muted-foreground">
                    {selectedCustomer.phone && (
                      <div className="flex items-center gap-2">
                        <Phone className="h-3 w-3" /> {selectedCustomer.phone}
                      </div>
                    )}
                    {selectedCustomer.email && (
                      <div className="flex items-center gap-2">
                        <Mail className="h-3 w-3" /> {selectedCustomer.email}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-muted text-center">
                  <p className="text-2xl font-bold">{selectedCustomer.total_visits || 0}</p>
                  <p className="text-xs text-muted-foreground">Visitas</p>
                </div>
                <div className="p-3 rounded-xl bg-muted text-center">
                  <p className="text-2xl font-bold text-destructive">{selectedCustomer.no_shows || 0}</p>
                  <p className="text-xs text-muted-foreground">No-Shows</p>
                </div>
                <div className="p-3 rounded-xl bg-muted text-center">
                  <p className="text-2xl font-bold">${(selectedCustomer.total_spent || 0).toLocaleString()}</p>
                  <p className="text-xs text-muted-foreground">Total</p>
                </div>
                <div className="p-3 rounded-xl bg-muted text-center">
                  <p className="text-2xl font-bold">${(selectedCustomer.average_spend || 0).toFixed(0)}</p>
                  <p className="text-xs text-muted-foreground">Promedio</p>
                </div>
              </div>

              {/* Allergies & Dietary */}
              {(selectedCustomer.allergies?.length > 0 || selectedCustomer.dietary_restrictions?.length > 0) && (
                <div className="p-4 rounded-xl bg-orange-50 dark:bg-orange-950/20 border border-orange-200 dark:border-orange-900">
                  <p className="font-medium text-orange-700 dark:text-orange-300 mb-2 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Información importante
                  </p>
                  {selectedCustomer.allergies?.length > 0 && (
                    <p className="text-sm text-orange-600 dark:text-orange-400">
                      <strong>Alergias:</strong> {selectedCustomer.allergies.join(', ')}
                    </p>
                  )}
                  {selectedCustomer.dietary_restrictions?.length > 0 && (
                    <p className="text-sm text-orange-600 dark:text-orange-400 mt-1">
                      <strong>Restricciones:</strong> {selectedCustomer.dietary_restrictions.join(', ')}
                    </p>
                  )}
                </div>
              )}

              {/* Preferences */}
              {selectedCustomer.seating_preference && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="h-4 w-4 text-muted-foreground" />
                  <span>Zona preferida: <strong>{selectedCustomer.seating_preference}</strong></span>
                </div>
              )}

              {/* Notes */}
              {selectedCustomer.notes && (
                <div>
                  <p className="text-sm font-medium mb-2">Notas</p>
                  <p className="text-sm text-muted-foreground bg-muted p-3 rounded-xl">
                    {selectedCustomer.notes}
                  </p>
                </div>
              )}

              {/* Visit History */}
              <div>
                <p className="text-sm font-medium mb-2 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Historial de visitas
                </p>
                {customerHistory.length === 0 ? (
                  <p className="text-sm text-muted-foreground">Sin historial de reservaciones</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {customerHistory.slice(0, 10).map((res) => (
                      <div key={res.id} className="flex items-center justify-between p-2 rounded-xl bg-muted text-sm">
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3 text-muted-foreground" />
                          <span>{res.date} {res.time}</span>
                        </div>
                        <Badge variant="outline">
                          {res.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDetailsDialog(false)} className="rounded-xl">
              Cerrar
            </Button>
            <Button onClick={() => {
              setShowDetailsDialog(false);
              openEditDialog(selectedCustomer);
            }} className="rounded-xl">
              <Edit2 className="h-4 w-4 mr-1" /> Editar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Coupon Dialog */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md rounded-2xl" data-testid="send-coupon-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Tag className="h-5 w-5 text-primary" />
              Enviar Cupón
            </DialogTitle>
            <DialogDescription>
              Envía un código promocional a {couponCustomer?.name}
            </DialogDescription>
          </DialogHeader>

          {couponCustomer && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className="flex items-center gap-3 p-3 rounded-xl bg-muted">
                <div className={cn(
                  "w-10 h-10 rounded-xl flex items-center justify-center",
                  couponCustomer.tags?.includes('VIP') 
                    ? "bg-gradient-to-br from-yellow-400 to-orange-500 text-white" 
                    : "bg-primary/10"
                )}>
                  {couponCustomer.tags?.includes('VIP') ? (
                    <Star className="h-5 w-5" />
                  ) : (
                    <User className="h-5 w-5 text-primary" />
                  )}
                </div>
                <div>
                  <p className="font-medium">{couponCustomer.name}</p>
                  <div className="flex items-center gap-3 text-xs text-muted-foreground">
                    {couponCustomer.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {couponCustomer.email}
                      </span>
                    )}
                    {couponCustomer.phone && (
                      <span className="flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {couponCustomer.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              {/* Select Promo Code */}
              <div className="space-y-2">
                <Label>Seleccionar cupón</Label>
                {promoCodes.length === 0 ? (
                  <div className="p-4 rounded-xl bg-muted text-center text-sm text-muted-foreground">
                    <Gift className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No hay cupones activos</p>
                    <p className="text-xs mt-1">Crea uno en la sección de Marketing</p>
                  </div>
                ) : (
                  <Select value={selectedPromoCode} onValueChange={setSelectedPromoCode}>
                    <SelectTrigger className="rounded-xl" data-testid="select-promo-code">
                      <SelectValue placeholder="Selecciona un cupón" />
                    </SelectTrigger>
                    <SelectContent>
                      {promoCodes.map((promo) => (
                        <SelectItem key={promo.id} value={promo.id}>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="font-mono">
                              {promo.code}
                            </Badge>
                            <span className="text-muted-foreground">
                              {promo.discount_type === 'percentage' 
                                ? `${promo.discount_value}%` 
                                : `$${promo.discount_value}`
                              }
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {/* Preview selected coupon */}
              {selectedPromoCode && (
                <div className="p-4 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                  {(() => {
                    const promo = promoCodes.find(p => p.id === selectedPromoCode);
                    if (!promo) return null;
                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <Badge className="font-mono text-base px-3 py-1">
                            {promo.code}
                          </Badge>
                          <span className="text-lg font-bold text-primary flex items-center gap-1">
                            {promo.discount_type === 'percentage' ? (
                              <><Percent className="h-4 w-4" /> {promo.discount_value}%</>
                            ) : (
                              <><DollarSign className="h-4 w-4" /> {promo.discount_value}</>
                            )}
                          </span>
                        </div>
                        {promo.description && (
                          <p className="text-sm text-muted-foreground">{promo.description}</p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Válido hasta: {promo.valid_until}
                        </p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Send method */}
              <div className="space-y-3">
                <Label>Método de envío</Label>
                <RadioGroup value={sendVia} onValueChange={setSendVia} className="grid grid-cols-3 gap-2">
                  <div>
                    <RadioGroupItem value="email" id="send-email" className="peer sr-only" />
                    <Label 
                      htmlFor="send-email" 
                      className={cn(
                        "flex flex-col items-center justify-center rounded-xl border-2 p-3 cursor-pointer transition-all",
                        "hover:bg-muted",
                        sendVia === 'email' ? "border-primary bg-primary/5" : "border-muted",
                        !couponCustomer?.email && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <Mail className="h-5 w-5 mb-1" />
                      <span className="text-xs">Email</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="sms" id="send-sms" className="peer sr-only" />
                    <Label 
                      htmlFor="send-sms" 
                      className={cn(
                        "flex flex-col items-center justify-center rounded-xl border-2 p-3 cursor-pointer transition-all",
                        "hover:bg-muted",
                        sendVia === 'sms' ? "border-primary bg-primary/5" : "border-muted",
                        !couponCustomer?.phone && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <MessageSquare className="h-5 w-5 mb-1" />
                      <span className="text-xs">SMS</span>
                    </Label>
                  </div>
                  <div>
                    <RadioGroupItem value="both" id="send-both" className="peer sr-only" />
                    <Label 
                      htmlFor="send-both" 
                      className={cn(
                        "flex flex-col items-center justify-center rounded-xl border-2 p-3 cursor-pointer transition-all",
                        "hover:bg-muted",
                        sendVia === 'both' ? "border-primary bg-primary/5" : "border-muted",
                        (!couponCustomer?.email || !couponCustomer?.phone) && "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <Send className="h-5 w-5 mb-1" />
                      <span className="text-xs">Ambos</span>
                    </Label>
                  </div>
                </RadioGroup>
                
                {/* Warnings */}
                {!couponCustomer?.email && !couponCustomer?.phone && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Este cliente no tiene email ni teléfono registrado
                  </p>
                )}
                {!couponCustomer?.email && sendVia === 'email' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Cliente sin email - selecciona SMS
                  </p>
                )}
                {!couponCustomer?.phone && sendVia === 'sms' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" />
                    Cliente sin teléfono - selecciona Email
                  </p>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setShowCouponDialog(false)} 
              className="rounded-xl"
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleSendCoupon}
              disabled={!selectedPromoCode || sendingCoupon || promoCodes.length === 0}
              className="rounded-xl"
              data-testid="send-coupon-btn"
            >
              {sendingCoupon ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-1 animate-spin" />
                  Enviando...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-1" />
                  Enviar cupón
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
